import {
  View,
  Pressable,
  ScrollView,
  Keyboard,
  type LayoutChangeEvent,
} from "@-/com.npmjs.react-native";
import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import runes from "@-/com.npmjs.runes2";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { HeaderScrollView } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { ToolbarViewButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import type { CustomLevelItem } from "@-/dev.improve.haniverse.lib.level.custom";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { getGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import { useCustomLevelsContext } from "#custom-levels-context";
import { CustomLevelsEditorSheet } from "#custom-levels-editor-sheet";
import { exportCustomLevelPack } from "#export-custom-level-pack";

export function CustomLevelsToolbarButtons() {
  const editor = useCustomLevelsContext();
  return (
    <>
      <ToolbarViewButton
        accessibilityRole="button"
        disabled={editor.isSaving}
        label="Cancel"
        onPress={() => {
          Keyboard.dismiss();
          editor.cancel();
        }}
        testID="custom-pack-cancel"
      />
      <ToolbarViewButton
        accessibilityRole="button"
        disabled={editor.isSaving}
        label="Tools"
        onPress={() => editor.openPanel("tools")}
        testID="custom-pack-tools"
      />
      <ToolbarViewButton
        accessibilityRole="button"
        disabled={editor.isSaving}
        label={editor.isSaving ? "Saving…" : "Save"}
        onPress={() => {
          Keyboard.dismiss();
          editor.save();
        }}
        selected
        testID="custom-pack-save"
      />
    </>
  );
}
function CustomPackItem({
  item,
  levelId,
  onLayout,
}: {
  item: CustomLevelItem;
  levelId: string;
  onLayout: (event: LayoutChangeEvent) => void;
}) {
  const editor = useCustomLevelsContext();
  const script = editor.pack?.script ?? "simplified";
  const navigator = useMorphemePanelsHrefNavigator();
  const selected = editor.selection?.itemId === item.id;
  const onPress = () => {
    if (editor.isEditing) {
      editor.setSelection({ itemId: item.id, levelId });
      editor.openPanel(item.kind === "slide" ? "slide" : "tools");
    } else if (item.kind === "card") {
      navigator.push.mutate({
        morpheme: item.morpheme,
        prefix: { href: "/(nav)/(app)/browse/morpheme/[morpheme]", params: { view: "mnemonics" } },
      });
    }
  };
  if (item.kind === "slide") {
    return (
      <Pressable
        accessibilityLabel={`Slide: ${item.title}. ${item.body}`}
        accessibilityRole={editor.isEditing ? "button" : "text"}
        className={`w-full gap-2 rounded-xs border p-4 ${selected ? "border-primary bg-primary/10" : "border-border bg-muted/50"}`}
        disabled={!editor.isEditing || editor.isSaving}
        onLayout={onLayout}
        onPress={onPress}
        testID={`custom-slide-${item.id}`}
      >
        <Text className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          Slide
        </Text>
        <Text className="text-xl font-semibold text-foreground">
          {item.title || "Untitled slide"}
        </Text>
        <Text className="text-base leading-6 text-foreground">
          {item.body || (editor.isEditing ? "Select this slide to add text." : "")}
        </Text>
      </Pressable>
    );
  }
  return (
    <Pressable
      accessibilityLabel={`${item.morpheme}, ${item.part}${editor.isEditing ? ", select card" : ""}`}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      className={`min-h-24 max-w-full min-w-20 items-center justify-center gap-1 rounded-xs border px-3 py-2 ${selected ? "border-primary bg-primary/10" : "border-border bg-background"}`}
      disabled={editor.isSaving}
      onLayout={onLayout}
      onPress={onPress}
      testID={`custom-card-${item.id}`}
    >
      <View className="max-w-full flex-row flex-wrap justify-center">
        {runes(item.morpheme).map((character, index) => (
          <GraphemeFontGlyph
            className="text-4xl leading-12 text-foreground"
            glyph={getGraphemeGlyph(character, { color: null })}
            hanziScript={script}
            key={`${character}-${index}`}
          />
        ))}
      </View>
      <Text className="text-[10px] text-muted-foreground">
        {item.part === "pronunciation"
          ? "Pronunciation"
          : item.part === "word"
            ? "Word"
            : "Character"}
      </Text>
    </Pressable>
  );
}

export function CustomLevelsPage() {
  const editor = useCustomLevelsContext();
  const scrollRef = useRef<ScrollView>(null);
  const levelOffsets = useRef(new Map<string, number>());
  const rowOffsets = useRef(new Map<string, number>());
  const itemOffsets = useRef(new Map<string, number>());
  const focusSelection = useCallback(() => {
    const selection = editor.selection;
    if (selection === null || !editor.isEditing) {
      return;
    }
    const levelY = levelOffsets.current.get(selection.levelId);
    const itemY =
      selection.itemId === undefined ? undefined : itemOffsets.current.get(selection.itemId);
    if (levelY === undefined) {
      return;
    }
    if (itemY !== undefined || selection.itemId === undefined) {
      const y =
        levelY +
        (selection.itemId === undefined
          ? 0
          : (rowOffsets.current.get(selection.levelId) ?? 0) + (itemY ?? 0));
      scrollRef.current?.scrollTo({ animated: true, y: Math.max(0, y - 8) });
    }
  }, [editor.selection, editor.isEditing]);
  useEffect(focusSelection, [focusSelection]);
  const exportMutation = useMutation({
    meta: { deadline: 11 * 60_000 },
    mutationFn: exportCustomLevelPack,
    mutationKey: ["exportCustomLevelPack"],
  });
  const { pack } = editor;
  return (
    <View className="relative min-h-0 flex-1">
      {editor.documentError !== null ? (
        <Text accessibilityRole="alert" className="p-4 text-destructive">
          {editor.documentError}
        </Text>
      ) : null}
      {pack !== null && !editor.isEditing ? (
        <View className="flex-row gap-2 px-4 py-2">
          <Button onPress={editor.create} size="sm" testID="custom-pack-new" variant="outline">
            <Text>New pack</Text>
          </Button>
          <Button onPress={editor.edit} size="sm" testID="custom-pack-edit">
            <Text>Edit pack</Text>
          </Button>
        </View>
      ) : null}
      {!editor.isEditing && editor.packs.length > 0 ? (
        <ScrollView
          className="h-14! flex-none!"
          contentContainerClassName="flex-row gap-2 px-4 py-2"
          horizontal
          showsHorizontalScrollIndicator={false}
        >
          {editor.packs.map((entry) => (
            <Button
              key={entry.id}
              onPress={() => editor.selectPack(entry.id)}
              size="sm"
              variant={entry.id === pack?.id ? "default" : "outline"}
            >
              <Text>{entry.name}</Text>
            </Button>
          ))}
        </ScrollView>
      ) : null}
      <HeaderScrollView
        contentContainerClassName={`gap-4 px-4 pt-4 ${editor.isEditing ? "pb-[70vh]" : "pb-32"}`}
        keyboardShouldPersistTaps="handled"
        ref={scrollRef}
      >
        {pack === null ? (
          <View className="items-center gap-4 rounded-xs border border-border bg-background p-8">
            <Text className="text-2xl font-bold">A curriculum of your own</Text>
            <Text className="text-center text-base leading-6 text-muted-foreground">
              Build a level pack with characters, words, and slides. Start fresh or copy levels from
              another pack.
            </Text>
            <Button
              disabled={editor.documentError !== null}
              onPress={editor.create}
              testID="custom-pack-create-empty"
            >
              <Text>Create a pack</Text>
            </Button>
          </View>
        ) : (
          <>
            <View className="gap-2">
              <Text className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                {editor.isEditing ? "Editing · live preview" : "Custom level pack"}
              </Text>
              <Text className="text-3xl font-bold text-foreground">
                {pack.name || "Untitled pack"}
              </Text>
              {pack.description ? (
                <Text className="text-base leading-6 text-muted-foreground">
                  {pack.description}
                </Text>
              ) : null}
              <Text className="text-sm text-muted-foreground">
                {pack.levels.length} levels ·{" "}
                {pack.script === "simplified" ? "Simplified" : "Traditional"}
              </Text>
              {!editor.isEditing ? (
                <Button
                  disabled={exportMutation.isPending}
                  onPress={() => exportMutation.mutate(pack)}
                  size="sm"
                  testID="custom-pack-export"
                  variant="outline"
                >
                  <Text>{exportMutation.isPending ? "Exporting…" : "Export JSON"}</Text>
                </Button>
              ) : null}
            </View>
            {editor.saveError ? (
              <Text accessibilityRole="alert" className="text-destructive">
                {editor.saveError}
              </Text>
            ) : null}
            {exportMutation.error ? (
              <Text accessibilityRole="alert" className="text-destructive">
                {exportMutation.error.message}
              </Text>
            ) : null}
            {pack.levels.length === 0 ? (
              <View className="gap-3 rounded-xs border border-dashed border-border bg-background/80 p-6">
                <Text className="text-lg font-semibold">The first level starts here</Text>
                <Text className="text-base leading-6 text-muted-foreground">
                  Use the tools below to add a level or copy one from Simplified, Traditional, or a
                  custom pack.
                </Text>
                {editor.isEditing ? (
                  <Button onPress={() => editor.openPanel("copy")} variant="outline">
                    <Text>Copy levels</Text>
                  </Button>
                ) : null}
              </View>
            ) : null}
            {pack.levels.map((level, index) => (
              <View
                className={`gap-3 rounded-xs border bg-background p-4 ${editor.selection?.levelId === level.id ? "border-primary/50" : "border-border"}`}
                key={level.id}
                onLayout={(event) => {
                  levelOffsets.current.set(level.id, event.nativeEvent.layout.y);
                  if (editor.selection?.levelId === level.id) {
                    focusSelection();
                  }
                }}
              >
                <Pressable
                  accessibilityLabel={`Level ${index + 1}: ${level.name}`}
                  accessibilityRole="button"
                  disabled={!editor.isEditing || editor.isSaving}
                  onPress={() => {
                    editor.setSelection({ levelId: level.id });
                    editor.openPanel("level");
                  }}
                >
                  <Text className="text-xs text-muted-foreground">Level {index + 1}</Text>
                  <Text className="text-xl font-bold">{level.name}</Text>
                  {level.description ? (
                    <Text className="mt-1 text-sm text-muted-foreground">{level.description}</Text>
                  ) : null}
                </Pressable>
                <View
                  className="flex-row flex-wrap gap-2"
                  onLayout={(event) => {
                    rowOffsets.current.set(level.id, event.nativeEvent.layout.y);
                    if (editor.selection?.levelId === level.id) {
                      focusSelection();
                    }
                  }}
                >
                  {level.items.map((item) => (
                    <CustomPackItem
                      item={item}
                      key={item.id}
                      levelId={level.id}
                      onLayout={(event) => {
                        itemOffsets.current.set(item.id, event.nativeEvent.layout.y);
                        if (editor.selection?.itemId === item.id) {
                          focusSelection();
                        }
                      }}
                    />
                  ))}
                </View>
                {level.items.length === 0 ? (
                  <Text className="text-sm text-muted-foreground">
                    Select this level and add cards from the tools below.
                  </Text>
                ) : null}
              </View>
            ))}
          </>
        )}
      </HeaderScrollView>
      {editor.isEditing ? <CustomLevelsEditorSheet /> : null}
    </View>
  );
}
